import { Inspectable } from "@constants/Inspector";
import { buildLibraryAssetUrl } from "@src/features/library/utils";
import { buildURL, ItemType, slugifyName } from "@thenounproject/lingo-core";
import { ActionContext } from "./types";
import { isOnlyColorValue } from "@src/helpers/colorPaste";

export const getShareLink = (ctx: ActionContext, inspectable: Inspectable) => {
  const { item, asset } = inspectable;

  if (item) {
    let path = "",
      hash = "",
      assetToken = undefined;
    switch (item.type) {
      case ItemType.asset:
        // Link to asset detail
        path = ["a", slugifyName(item.asset?.name, item.shortId)].join("/");
        assetToken = item.asset?.shareToken;
        break;
      case ItemType.gallery: {
        //Link to gallery
        path = `a/${item.urlId}`;
        break;
      }
      default: {
        // Link to item in the section

        let sectionId = item.sectionId;
        const section = ctx.data.sections[`${sectionId}-${item.version}`];
        if (section?.shortId) {
          sectionId = slugifyName(section.name, section.shortId);
        }
        path = `/s/${sectionId}`;
        hash = item.shortId;
      }
    }
    return buildURL(
      path,
      { space: ctx.location.space, portal: ctx.location.portal, isPublic: true },
      { v: item.version, asset_token: assetToken },
      hash
    );
  } else if (asset) {
    // Library link
    return buildLibraryAssetUrl(asset, ctx.location.space);
  }
};

export const cleanedTagInput = (ctx: ActionContext) =>
  ctx.userInput?.replace(/(add|tag|keyword)/g, "").trim();

export const isAsset = (inspectable: Inspectable) =>
  !inspectable.item || inspectable.item.type === ItemType.asset;

export const assetHasFile = (inspectable: Inspectable) => Boolean(inspectable.asset?.fileId);

export const assetsWithFiles = (ctx: ActionContext) => ctx.selection.assets.filter(assetHasFile);

export const isDownloadable = ({ item, asset }: Inspectable) => {
  return (
    asset?.fileId &&
    (!item || (item?.displayProperties?.allowDownload !== false && item?.type === ItemType.asset))
  );
};

export const canApplyPreivewBackground = (ctx: ActionContext) =>
  ctx.selection.assets.some(({ asset }) => asset.type !== "color" && asset.type !== "textStyle");

const CARD_COLOR_PRESETS = ["Success", "Info", "Warning", "Neutral", "Transparent", "Clear"];
export const cardBackgroundColor = (ctx: ActionContext) => {
  const colorInput = ctx.userInput
    .toLowerCase()
    .replace("card", "")
    .replace("background", "")
    .trim();
  const presetMatch =
    colorInput.length > 2 &&
    CARD_COLOR_PRESETS.find(preset => preset.toLocaleLowerCase().startsWith(colorInput));
  if (presetMatch) {
    return presetMatch;
  }
  return isOnlyColorValue(colorInput) ? colorInput.toUpperCase() : null;
};

const PREVIEW_COLOR_PRESETS = ["Light", "Dark", "Checkered", "Transparent", "Clear"];
export const previewBackgroundColor = (ctx: ActionContext) => {
  const colorInput = ctx.userInput
    .replace("preview", "")
    .replace("background", "")
    .toLowerCase()
    .trim();

  const presetMatch =
    colorInput.length > 2 &&
    PREVIEW_COLOR_PRESETS.find(preset => preset.toLowerCase().startsWith(colorInput));
  if (presetMatch) {
    return presetMatch;
  }
  return isOnlyColorValue(colorInput) ? colorInput.toUpperCase() : null;
};

export const colorValue = (ctx: ActionContext) => {
  const colorInput = ctx.userInput.trim().toLowerCase();
  return isOnlyColorValue(colorInput) ? colorInput.toUpperCase() : null;
};
